import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { IconButton } from './icon-button';

type PlaybackSpeedProps = {
  playbackRate: number;
  speedOptions: number[];
  playbackSpeedOpen: boolean;
  setPlaybackSpeedOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectPlaybackRate: (rate: number) => void;
  isMobileFullPlayer?: boolean;
  iconAssets: { speed: string };
};

/** Renders playback speed control and its selection menu. */
export function PlaybackSpeed({
  playbackRate,
  speedOptions,
  playbackSpeedOpen,
  setPlaybackSpeedOpen,
  onSelectPlaybackRate,
  iconAssets,
  isMobileFullPlayer,
}: PlaybackSpeedProps): JSX.Element {
  useEffect(() => {
    if (!playbackSpeedOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      setPlaybackSpeedOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [playbackSpeedOpen, setPlaybackSpeedOpen]);

  return (
    <div className="relative">
      <IconButton
        src={iconAssets.speed}
        alt="Playback speed"
        onClick={() => setPlaybackSpeedOpen((v) => !v)}
        size={isMobileFullPlayer ? 44 : 40}
        iconSize={isMobileFullPlayer ? 24 : 20}
        buttonClassName={`group flex ${
          isMobileFullPlayer ? 'h-[44px] w-[44px]' : 'h-[40px] w-[40px]'
        } items-center justify-center rounded-full transition-colors audio-player-action_button_background_color_default audio-player-action_button_background_color_hover audio-player-action_button_background_color_pressed audio-player-action_button_background_color_active audio-player-action_button_background_default audio-player-action_button_background_hover audio-player-action_button_background_pressed audio-player-action_button_background_active`}
        iconClassName={`${
          isMobileFullPlayer ? 'h-[24px] w-[24px]' : 'h-[20px] w-[20px]'
        } audio-player-action_button_asset_color_default audio-player-action_button_asset_color_hover audio-player-action_button_asset_color_pressed audio-player-action_button_asset_color_active`}
        iconColor={'var(--action_button_asset_color_default)'}
      />
      {playbackSpeedOpen && (
        <>
          {isMobileFullPlayer && (
            <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-md" />
          )}

          <div
            className={twMerge(
              'absolute bottom-full right-[-80px] z-50 mb-2 w-[195px] rounded-2xl bg-[#121212] p-4 text-white shadow-2xl',
              isMobileFullPlayer &&
                'fixed left-1/2 top-1/2 h-[380px] w-full max-w-[500px] -translate-x-1/2 -translate-y-1/2'
            )}
          >
            <p
              className={twMerge(
                'mb-3 text-[12px] font-medium text-neutral-400',
                isMobileFullPlayer && 'text-[14px]'
              )}
            >
              Playback Speed
            </p>
            <div
              className={twMerge(
                'flex flex-col gap-0.5',
                isMobileFullPlayer && 'gap-[16px]'
              )}
            >
              {speedOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    onSelectPlaybackRate(option);
                    setPlaybackSpeedOpen(false);
                  }}
                  className="flex w-full items-center justify-between py-1.5 text-left text-[15px] font-semibold text-neutral-300 transition-colors hover:text-white"
                >
                  <span
                    className={twMerge(
                      'text-[12px]',
                      isMobileFullPlayer && 'text-[16px]'
                    )}
                  >
                    {option === 1 ? '1x' : `${option}x`}
                  </span>

                  {playbackRate === option && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                      className="h-4 w-4 text-neutral-300"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m4.5 12.75 6 6 9-13.5"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {isMobileFullPlayer && (
              <button
                className="mt-[16px] h-[48px] w-full rounded-[12px] bg-[rgba(62,62,62,1)]"
                onClick={() => setPlaybackSpeedOpen(false)}
              >
                Close
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
